import { useRef, useState } from 'react';
import { pageRotation, nextRotation } from './pageRotation';

// The admin's page turns on DocumentViewPage (owner, 19/10/2026): each click
// shows the next quarter turn at once and saves it; a failed save puts the
// page back to the last angle the server accepted.
//
// Turns are tied to the document's ID, not to the `doc` object. The page
// replaces `doc` on every reload AND on in-place updates (a recipient alias,
// say); wiping the turns then would show a page turned back that the server
// already holds turned. They are also kept out of `doc.files`, because
// several of the page's effects re-run on every `doc` change, the scan
// re-fetch among them.
//
// Saves go out one after another, so quick clicks can never land on the
// server out of order — each sends its own absolute angle, so the last
// successful one is what the server holds.
//
// Accepted limit (PR #293 review): within one visit to a document, the turns
// made here win over a reload. If another admin turns the SAME page in the
// meantime, this screen keeps showing its own angle until the user leaves the
// document, and its next click saves over theirs. Reconciling with the reload
// would need `doc.files` patched on every save (which re-runs the scan fetch)
// or would show a page turned back after an in-place `doc` update — both
// worse than a two-admins-one-page race.
//
//   doc      — the loaded document ({ id, files })
//   save     — (docId, pageNumber, rotation) => Promise
//   onError  — (err) => void, after a failed save has been rolled back
//   returns  { withRotations(files) → files with the turns applied,
//              rotatePage(file) }
export function usePageRotations(doc, { save, onError }) {
  const [edits, setEdits] = useState({ docId: null, pages: {} });
  const chainRef = useRef(Promise.resolve());
  // Last angle the server accepted, per page, for the document turned last.
  const confirmedRef = useRef({ docId: null, pages: {} });
  // The angle each page was last turned TO, updated in the click itself: two
  // taps inside one render (a fast double-tap) would otherwise both start
  // from the same not-yet-rendered angle and one turn would be lost.
  const latestRef = useRef({ docId: null, pages: {} });

  const withRotations = (files) => files.map(f => {
    const turned = doc && edits.docId === doc.id ? edits.pages[f.page_number] : undefined;
    return turned != null ? { ...f, rotation: turned } : f;
  });

  const rotatePage = (file) => {
    const page = file?.page_number;
    if (!doc || !page) return;
    const docId = doc.id;
    if (latestRef.current.docId !== docId) latestRef.current = { docId, pages: {} };
    const latest = latestRef.current;
    const next = nextRotation(latest.pages[page] ?? pageRotation(file));
    latest.pages[page] = next;
    if (confirmedRef.current.docId !== docId) confirmedRef.current = { docId, pages: {} };
    const confirmed = confirmedRef.current;
    if (confirmed.pages[page] == null) {
      confirmed.pages[page] = pageRotation((doc.files || []).find(f => f.page_number === page));
    }
    setEdits(r => ({ docId, pages: { ...(r.docId === docId ? r.pages : {}), [page]: next } }));
    chainRef.current = chainRef.current
      .then(() => save(docId, page, next))
      .then(() => { confirmed.pages[page] = next; })
      .catch(err => {
        // Only if no later click has turned it since (that click's own save
        // decides where the page ends up), and only on this same document — a
        // late failure must not touch the next draft after אשר והבא.
        if (latest.pages[page] === next) latest.pages[page] = confirmed.pages[page];
        setEdits(r => (r.docId === docId && r.pages[page] === next
          ? { docId, pages: { ...r.pages, [page]: confirmed.pages[page] } }
          : r));
        onError(err);
      });
  };

  return { withRotations, rotatePage };
}
